/**
 * Service Graph Builder
 *
 * Builds a directed graph from GeoEvent service flow connections.
 */

import type {
  GeoEventConfiguration,
  GeoEventService,
  Input,
  Output,
  ServiceNode,
} from '../types/geoevent.js';
import type { GraphNode, GraphEdge, ServiceGraph, GraphNodeType } from '../types/graph.js';

/**
 * Determines the node type for a service node
 */
function getServiceNodeType(node: ServiceNode): GraphNodeType {
  if (node.filter) {
    return 'filter';
  }
  if (node.processor) {
    return 'processor';
  }
  return 'processor';
}

/**
 * Builds a graph from a GeoEvent service and its configuration context
 */
export function buildServiceGraph(
  service: GeoEventService,
  config: GeoEventConfiguration
): ServiceGraph {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeMap = new Map<string, GraphNode>();

  // Add service inputs as graph nodes
  for (const serviceInput of service.inputs) {
    // Find the global input definition
    const globalInput = config.inputs.find((i) => i.name === serviceInput.inputRef);

    const node: GraphNode = {
      id: serviceInput.name,
      label: serviceInput.label || serviceInput.name,
      type: 'input',
      component: globalInput || ({
        name: serviceInput.name,
        label: serviceInput.label,
        connector: '',
        connectorLabel: '',
        adapter: { uri: '', properties: {} },
        transport: { uri: '', properties: {} },
        runningState: 'STOPPED',
      } as Input),
    };
    nodes.push(node);
    nodeMap.set(serviceInput.name, node);
  }

  // Add service outputs as graph nodes
  for (const serviceOutput of service.outputs) {
    // Find the global output definition
    const globalOutput = config.outputs.find((o) => o.name === serviceOutput.outputRef);

    const node: GraphNode = {
      id: serviceOutput.name,
      label: serviceOutput.label || serviceOutput.name,
      type: 'output',
      component: globalOutput || ({
        name: serviceOutput.name,
        label: serviceOutput.label,
        connector: '',
        connectorLabel: '',
        adapter: { uri: '', properties: {} },
        transport: { uri: '', properties: {} },
        runningState: 'STOPPED',
      } as Output),
    };
    nodes.push(node);
    nodeMap.set(serviceOutput.name, node);
  }

  // Add service nodes (filters and processors)
  for (const serviceNode of service.nodes) {
    const node: GraphNode = {
      id: serviceNode.name,
      label: serviceNode.label || serviceNode.name,
      type: getServiceNodeType(serviceNode),
      component: serviceNode,
    };
    nodes.push(node);
    nodeMap.set(serviceNode.name, node);
  }

  // Build edges from flow connections
  for (const flow of service.flow) {
    const fromId = flow.fromRef;

    for (const target of flow.toRefs) {
      const toId = target.ref;

      // Only add edge if both nodes exist
      if (nodeMap.has(fromId) && nodeMap.has(toId)) {
        edges.push({
          from: fromId,
          to: toId,
        });
      }
    }
  }

  return { nodes, edges };
}
